import { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { useConfig, useFeatures } from "@/config/context"
import { useTranslation } from "@/i18n"
import { useTimeline } from "@/hooks/useTimeline"
import { usePostFilters } from "@/hooks/usePostFilters"
import { useFilteredPosts } from "@/hooks/useFilteredPosts"
import { useRelayDiscovery, useRelayReviewFeed } from "@/hooks/useRelayData"
import { useRelays } from "@/state/RelayContext"
import { useSettings } from "@/state/SettingsContext"
import { Tabs } from "@/components/Tabs"
import { RelayCard } from "@/components/RelayCard"
import { NoteList } from "@/components/NoteList"
import { PostTypeFilter } from "@/components/PostTypeFilter"
import { PostFilters } from "@/components/filters/PostFilters"
import { EmptyState, Skeleton, Spinner } from "@/components/Feedback"
import { StarRating } from "@/components/StarRating"
import { Icon } from "@/components/Icon"
import { Timestamp } from "@/components/Timestamp"
import { postTypeSelection, relayFilterHints } from "@/nostr/api/filters/filterEngine"
import { simplifyUrl } from "@/utils/url"

type ExploreTab = "discover" | "relays" | "reviews"

/**
 * Explore: trending notes from the trending relay, curated relay collections
 * (awesome-nostr-relays + NIP-66 discoveries) and relay reviews.
 */
export function ExplorePage() {
  const { t } = useTranslation()
  const config = useConfig()
  const features = useFeatures()
  const { favoriteRelays } = useRelays()
  const { settings } = useSettings()
  const [tab, setTab] = useState<ExploreTab>("discover")
  const [filter, setFilter] = useState("")
  const postFilters = usePostFilters("explore")
  const filters = postFilters.filters
  const hints = useMemo(() => relayFilterHints(filters), [filters])

  const trending = useTimeline({
    relays: config.relays.trendingRelays,
    postTypes: filters.postTypes,
    since: hints.since,
    until: hints.until,
    enabled: tab === "discover" && features.trending,
    limit: 25,
  })
  const visible = useFilteredPosts(trending.events, filters, { mutedWords: settings.mutedWords })
  const discovery = useRelayDiscovery()
  const reviews = useRelayReviewFeed()

  const collections = useMemo(() => {
    const term = filter.trim().toLowerCase()
    if (!term) return discovery.collections
    return discovery.collections
      .map((collection) => ({
        ...collection,
        relays: collection.relays.filter((url) => url.toLowerCase().includes(term)),
      }))
      .filter((collection) => collection.relays.length > 0)
  }, [discovery.collections, filter])

  const tabs = [
    { value: "discover", label: t("Discover") },
    { value: "relays", label: t("Explore Relays") },
    ...(features.relayReviews ? [{ value: "reviews", label: t("Relay reviews") }] : []),
  ]

  return (
    <div className="page explore-page">
      <header className="page-header">
        <div className="page-header-main">
          <h1 className="page-title">{t("Explore")}</h1>
          <p className="page-subtitle">
            {favoriteRelays.length} {t("Relays")} ·{" "}
            {discovery.collections.reduce((sum, collection) => sum + collection.relays.length, 0)} in
            collections
          </p>
        </div>
      </header>

      <Tabs
        value={tab}
        tabs={tabs}
        onChange={(value) => setTab(value as ExploreTab)}
        ariaLabel={t("Explore")}
      />

      {tab === "discover" && (
        <section className="explore-section">
          <div className="section-head">
            <h2>{t("Trending notes")}</h2>
            <span className="muted small">wss://trending.relays.land</span>
          </div>
          {!features.trending ? (
            <EmptyState
              icon="info"
              title={t("Trending is disabled")}
              description="Enable it in the feature flags."
            />
          ) : (
            <>
              <div className="filter-bar">
                <PostTypeFilter
                  value={postTypeSelection(filters)}
                  onChange={(selection) =>
                    postFilters.patch({ postTypes: selection === "all" ? [] : [selection] })
                  }
                />
                <PostFilters
                  value={filters}
                  active={postFilters.active}
                  onApply={(next) => postFilters.patch(next)}
                  onClearAll={postFilters.clearAll}
                  onRemove={postFilters.remove}
                  categories={["content", "time"]}
                  mutedWordCount={settings.mutedWords.length}
                />
              </div>
              {visible.hidden > 0 && (
                <p className="muted small filter-hidden-note">
                  {t("{n} posts hidden by the active filters", { n: visible.hidden })}
                </p>
              )}
              <NoteList
                events={visible.events}
                loading={trending.loading}
                loadingMore={trending.loadingMore}
                hasMore={trending.hasMore}
                onLoadMore={trending.loadMore}
                emptyTitle={
                  postFilters.isDefault
                    ? t("No trending notes right now")
                    : t("No posts match the filters")
                }
                emptyDescription={
                  postFilters.isDefault
                    ? t("The trending relay did not return any notes.")
                    : t("The trending relay has no posts matching the selected filters.")
                }
              />
            </>
          )}
        </section>
      )}

      {tab === "relays" && (
        <section className="explore-section">
          <div className="relay-filter">
            <Icon name="search" size={16} />
            <input
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              placeholder={t("Filter relays by address")}
              aria-label={t("Filter relays by address")}
            />
          </div>

          {discovery.error && (
            <div className="banner banner-warn" role="status">
              <Icon name="alert" size={16} />
              <div>
                <p>{t("The curated relay list could not be loaded.")}</p>
                <p className="muted small">{discovery.error}</p>
              </div>
            </div>
          )}

          {discovery.loading && collections.length === 0 && (
            <div className="relay-grid">
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={index} lines={2} />
              ))}
            </div>
          )}

          {!discovery.loading && collections.length === 0 && (
            <EmptyState
              icon="relay"
              title={t("No relays found")}
              description={t("Try a different filter, or add a relay manually in Settings.")}
              action={
                <Link to="/settings#relays" className="btn btn-primary">
                  {t("Manage relays")}
                </Link>
              }
            />
          )}

          {collections.map((collection) => (
            <div key={collection.id} className="relay-collection">
              <div className="section-head">
                <h2>{collection.name}</h2>
                <span className="muted small">{collection.relays.length}</span>
              </div>
              <div className="relay-grid">
                {collection.relays.slice(0, 60).map((url) => (
                  <RelayCard key={url} url={url} />
                ))}
              </div>
              {collection.relays.length > 60 && (
                <p className="muted small">Showing the first 60 relays of this collection.</p>
              )}
            </div>
          ))}
        </section>
      )}

      {tab === "reviews" && (
        <section className="explore-section">
          <div className="section-head">
            <h2>{t("Relay reviews")}</h2>
            <span className="muted small">kind 31987</span>
          </div>
          {reviews.loading && reviews.reviews.length === 0 && <Spinner />}
          {!reviews.loading && reviews.reviews.length === 0 && (
            <EmptyState
              icon="star"
              title={t("No relay reviews yet")}
              description={t("Open a relay and write the first review.")}
            />
          )}
          <ul className="review-list">
            {reviews.reviews.map((review) => (
              <li key={`${review.relayUrl}-${review.pubkey}`} className="review-card">
                <div className="review-head">
                  <Link to={`/relay/${encodeURIComponent(review.relayUrl)}`} className="review-relay">
                    {simplifyUrl(review.relayUrl)}
                  </Link>
                  <StarRating value={review.stars} />
                  <Timestamp seconds={review.createdAt} className="muted small" />
                </div>
                {review.content && <p className="review-content">{review.content}</p>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}
